import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * Step 2 of password reset: enter email + code + new password.
 *
 * If the user clicks the link from the reset email, email and code arrive
 * via query params and are pre-filled — they just have to choose a password.
 * If they paste the code manually, all three fields are editable.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState(params.get('code') ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Briefly show success then bounce to /auth/signin.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => navigate('/auth/signin'), 2000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.passwordResetConfirm({ email, code, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(friendlyError(err, 'ResetPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ maxWidth: 440, margin: '0 auto', padding: '80px 24px' }}>
      <Link to="/auth/signin" style={{ color: 'var(--ink-3)', fontSize: 13 }}>← back</Link>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginTop: 32, marginBottom: 14 }}
      >
        Reset password
      </div>
      <h1
        className="serif"
        style={{ fontSize: 36, lineHeight: 1.05, letterSpacing: -0.8, fontWeight: 500, marginBottom: 8 }}
      >
        Pick a <em>new</em> one.
      </h1>
      <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 28 }}>
        Paste the 8-digit code from your email and choose a new password.
      </p>

      {done ? (
        <div style={successBox}>
          Password updated. Redirecting to sign in…
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Email">
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={input}
            />
          </Field>
          <Field label="Reset code">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{8}"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              style={{ ...input, fontFamily: 'var(--font-mono)', letterSpacing: 4, textAlign: 'center' }}
            />
          </Field>
          <Field label="New password">
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={input}
            />
            <small style={{ color: 'var(--ink-3)', fontSize: 12 }}>≥ 8 characters, must contain a number.</small>
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              style={input}
            />
          </Field>
          {error && (
            <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
              {error}
            </div>
          )}
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      )}
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const successBox: React.CSSProperties = {
  background: 'rgba(56,142,60,0.08)',
  border: '1px solid rgba(56,142,60,0.2)',
  color: '#2e7d32',
  borderRadius: 12,
  padding: '14px 16px',
  fontSize: 13 };

const input: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface)',
  border: '1.5px solid transparent',
  borderRadius: 14,
  padding: '14px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink)',
  outline: 'none' };

const btnPrimary: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  padding: '16px 24px',
  borderRadius: 100,
  fontWeight: 600,
  fontSize: 15,
  boxShadow: 'var(--shadow-card)',
  border: 'none',
  cursor: 'pointer' };
